import { Alert, Box, Button, Stack } from "@mui/material";
import { type RateConfiguration } from "@src/appV2/ExperimentalHomeHealth/lib/rateConfigurationUtils";
import SvgCheck from "@src/appV2/redesign/assets/illustrations/Check";
import { getFloatingNavigationScrollPaddingBottom } from "@src/appV2/redesign/components/FloatingNavigation/scrollPadding";
import { Text } from "@src/appV2/redesign/components/Text";
import { Title } from "@src/appV2/redesign/components/Title";

import { VisitType } from "../types";
import { MobileCareTeamReview } from "./careTeam/MobileCareTeamReview";
import { MobileCaseDetailsFields, type CaseDetailsData } from "./careTeam/MobileCaseDetailsFields";
import { MobileVisitDisciplineFields } from "./careTeam/MobileVisitDisciplineFields";
import { MobileVisitScheduleFields } from "./careTeam/MobileVisitScheduleFields";
import {
  type VisitDraft,
  type VisitDraftFieldValue,
  type VisitDraftFields,
} from "./careTeam/visitDraft";
import { type SelectedAddress } from "./MobileAddressAutocompleteField";
import { MobileCreateCaseStep1, type Step1Data } from "./MobileCreateCaseStep1";
import { MobileCreateCaseSuccess } from "./MobileCreateCaseSuccess";
import { type MobileCaseStep2FieldErrors } from "./mobileCaseStep2Validation";
import { MobileHomeHealthPageHeader } from "./MobileHomeHealthPageHeader";

/** Which screen of the care-team wizard is currently shown. */
export type CareTeamWizardScreen =
  | "success"
  | "patient"
  | "discipline"
  | "schedule"
  | "review"
  | "details";

interface ButtonState {
  label: string;
  disabled: boolean;
  onClick: () => void;
}

interface Props {
  title: string;
  /** Progress-bar segment labels and the index of the active segment. */
  progressSteps: readonly string[];
  activeStepIndex: number;
  screen: CareTeamWizardScreen;
  submitError: string | null;

  /** Patient step (create-case only; omit for the add-visit flow). */
  patient?: {
    data: Step1Data;
    addressLocked: boolean;
    onChange: (field: keyof Step1Data, value: string | boolean) => void;
    onAddressSelect: (address: SelectedAddress) => void;
  };

  /** The visit currently being configured (discipline + schedule screens). */
  editorFields: VisitDraftFields;
  rates: RateConfiguration;
  scheduleErrors: MobileCaseStep2FieldErrors;
  onEditorFieldChange: (field: keyof VisitDraftFields, value: VisitDraftFieldValue) => void;

  /** Case-level details screen. */
  caseDetails: CaseDetailsData;
  onCaseDetailsChange: (field: keyof CaseDetailsData, value: string | string[]) => void;

  /** Care-team review screen. */
  drafts: VisitDraft[];
  onAddAnother: () => void;
  onEditDraft: (draftId: string) => void;
  onRemoveDraft: (draftId: string) => void;

  /** Success screen. */
  successVisitCount: number;
  successVariant: "case" | "visits";
  onDone: () => void;

  back: ButtonState;
  primary: ButtonState;
}

const buttonBaseSx = {
  flex: 1,
  borderRadius: "24px",
  py: 1.5,
  textTransform: "none",
  fontSize: "1rem",
} as const;

/** Presentational shell for both mobile care-team wizards; state lives in the containers. */
export function MobileCareTeamWizardView({
  title,
  progressSteps,
  activeStepIndex,
  screen,
  submitError,
  patient,
  editorFields,
  rates,
  scheduleErrors,
  onEditorFieldChange,
  caseDetails,
  onCaseDetailsChange,
  drafts,
  onAddAnother,
  onEditDraft,
  onRemoveDraft,
  successVisitCount,
  successVariant,
  onDone,
  back,
  primary,
}: Props) {
  const isSuccess = screen === "success";
  const paddingX = 3;

  return (
    <Stack
      sx={(theme) => ({
        // Fill (not exceed) the fixed-height page wrapper so the footer stays pinned
        // and only the middle section scrolls.
        height: "100%",
        minHeight: 0,
        bgcolor: "background.baseDark",
        // Clear the floating Menu pill and iOS home bar so the footer isn't overlapped.
        paddingBottom: getFloatingNavigationScrollPaddingBottom(theme) ?? "4rem",
      })}
    >
      <Stack sx={{ px: paddingX, pt: 4, gap: 3 }}>
        <MobileHomeHealthPageHeader title={title} />
        {!isSuccess && (
          <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
            {progressSteps.map((key, index) => (
              <Box
                key={key}
                sx={{
                  flex: 1,
                  height: 8,
                  borderRadius: 3,
                  bgcolor: index <= activeStepIndex ? "base.secondary" : "transparent",
                  border: "1px solid",
                  borderColor: "base.secondary",
                }}
              />
            ))}
          </Stack>
        )}
      </Stack>

      <Stack sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: paddingX, py: 3 }}>
        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {submitError}
          </Alert>
        )}
        {isSuccess ? (
          successVariant === "case" ? (
            <MobileCreateCaseSuccess visitCount={successVisitCount} />
          ) : (
            <Stack alignItems="center" justifyContent="center" spacing={3} sx={{ flex: 1, px: 3 }}>
              <SvgCheck />
              <Title component="h5" sx={{ fontWeight: "bold" }}>
                {successVisitCount} {successVisitCount === 1 ? "visit" : "visits"} posted
              </Title>
              <Text variant="body1" sx={{ textAlign: "center" }}>
                The visits are now open and visible to local professionals!
              </Text>
            </Stack>
          )
        ) : screen === "patient" && patient ? (
          <MobileCreateCaseStep1
            data={patient.data}
            addressLocked={patient.addressLocked}
            onChange={patient.onChange}
            onAddressSelect={patient.onAddressSelect}
          />
        ) : screen === "details" ? (
          <MobileCaseDetailsFields data={caseDetails} onChange={onCaseDetailsChange} />
        ) : screen === "discipline" ? (
          <MobileVisitDisciplineFields data={editorFields} onChange={onEditorFieldChange} />
        ) : screen === "schedule" ? (
          <MobileVisitScheduleFields
            data={editorFields}
            pricingType={editorFields.pricingType}
            visitType={(editorFields.visitType as VisitType) || undefined}
            rates={rates}
            errors={scheduleErrors}
            onChange={onEditorFieldChange}
          />
        ) : (
          <MobileCareTeamReview
            drafts={drafts}
            onAddAnother={onAddAnother}
            onEditDraft={onEditDraft}
            onRemoveDraft={onRemoveDraft}
          />
        )}
      </Stack>

      <Stack direction="row" spacing={2} sx={{ px: paddingX, pb: 4, pt: 2 }}>
        {isSuccess ? (
          <Button fullWidth variant="primary" onClick={onDone} sx={{ ...buttonBaseSx }}>
            OK
          </Button>
        ) : (
          <>
            <Button
              variant="secondary"
              disabled={back.disabled}
              onClick={back.onClick}
              sx={{ ...buttonBaseSx }}
            >
              Back
            </Button>
            <Button
              variant="primary"
              disabled={primary.disabled}
              onClick={primary.onClick}
              sx={{ ...buttonBaseSx }}
            >
              {primary.label}
            </Button>
          </>
        )}
      </Stack>
    </Stack>
  );
}
